import React, { useEffect, useRef, useState } from 'react'
import { UserPlus, Users, Ban } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { router, usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import Modal from './Modal'
import PatientSearchSelect from './PatientSearchSelect'
import DictateButton from './DictateButton'
import { useLanguage } from '../lib/LanguageContext'

// ── Appointment form modal (Create & Edit) ──────────────────────────
// A single component handles both Create and Edit because the form
// fields are identical — only the submit endpoint and defaults
// change. `mode` picks which:
//   - mode="create" → POST /appointments with a patient picker
//   - mode="edit"   → PATCH /appointments/:id, patient pre-filled
//
// Validation is zod + react-hook-form. Times are entered as HTML5
// datetime-local inputs for zero-dependency input UX; zod parses the
// string into a date and enforces end > start.

// Build the schema dynamically so validation messages use the current language.
// The schema itself is static (field names don't change), but we wrap it
// in a function so `t()` is called at resolve-time, not import-time.
// Default appointment duration in minutes, inferred from the reason text.
// Per the practice config: check-up 45m, whitening 90m, cleaning 30m, etc.
function defaultDurationFor(reason) {
  const r = (reason || '').toLowerCase()
  if (r.includes('whiten')) return 90
  if (r.includes('check') || r.includes('exam') || r.includes('cosmetic') || r.includes('aligner')) return 45
  if (r.includes('clean') || r.includes('hygien') || r.includes('polish') || r.includes('scal')) return 30
  if (r.includes('consult')) return 30
  if (r.includes('extract')) return 30
  if (r.includes('filling') || r.includes('restor') || r.includes('crown')) return 45
  return 30
}

// Format a Date as the value an HTML datetime-local input expects (local TZ).
function dtLocal(d) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// Snap a datetime-local string's minutes to the nearest 15-min interval.
function snapTo15(str) {
  if (!str) return str
  const d = new Date(str)
  const snapped = Math.round(d.getMinutes() / 15) * 15
  if (snapped === d.getMinutes()) return str
  d.setMinutes(snapped, 0, 0)
  return dtLocal(d)
}

function buildSchema(t, getNewPatientMode) {
  return z
    .object({
      // patient_id is only required when NOT in new-patient mode. When
      // the user toggles "New patient" the inline fields take over and
      // patient_id can be blank.
      patient_id: z.union([z.string(), z.number()]).optional(),
      start_time: z.string().min(1, t('validation_start_required')),
      end_time: z.string().min(1, t('validation_end_required')),
      duration: z.union([z.string(), z.number()]).optional(),
      reason: z.string().optional(),
      notes: z.string().optional(),
      asap: z.boolean().optional(),
      // MUST be in the schema or zodResolver strips it from the submitted data —
      // that was why the chosen dentist was lost (bookings defaulted to the active
      // dentist; Closed blocks got null → showed in BOTH columns).
      provider_id: z.union([z.string(), z.number()]).optional(),
    })
    .refine((v) => new Date(v.end_time) > new Date(v.start_time), {
      path: ['end_time'],
      message: t('validation_end_after_start'),
    })
    .refine((v) => (typeof getNewPatientMode === 'function' && getNewPatientMode()) || !!v.patient_id, {
      path: ['patient_id'],
      message: t('validation_patient_required'),
    })
}

// Convert an ISO string from the server to the value format an HTML
// datetime-local input expects: "YYYY-MM-DDTHH:mm" in *local* time.
const toLocalInput = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// 15-minute start-time slots (06:00–18:45) — RECEPTION's manual picker (early bookings; Paul 2026-08-05).
// This is the reception hand-book range ONLY — AI/patient booking is still governed by each provider's
// WorkingHours (08:00–17:00 Mon–Fri), not by this list.
const TIME_SLOTS = (() => {
  const out = []
  for (let m = 6 * 60; m <= 18 * 60 + 45; m += 15) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
  }
  return out
})()

// Duration presets. Block-out time (lunch, leave, a half/full day) is NOT capped — pick a long
// preset or "Custom…" for any number of minutes. (Previously the dropdown stopped at 120 min.)
const DURATION_PRESETS = [
  { m: 15, label: '15 min' },
  { m: 30, label: '30 min' },
  { m: 45, label: '45 min — check-up / cosmetic' },
  { m: 60, label: '60 min' },
  { m: 90, label: '90 min — whitening' },
  { m: 120, label: '120 min' },
  { m: 180, label: '3 hours' },
  { m: 240, label: '4 hours' },
  { m: 360, label: '6 hours' },
  { m: 480, label: '8 hours' },
  { m: 660, label: 'Full day (07:00–18:00)' },
]

export default function AppointmentFormModal({
  open,
  onClose,
  mode = 'create',       // 'create' | 'edit'
  appointment,           // required for mode=edit
  patients = [],         // required for mode=create
  prefillStart = null,   // Date | null — opened from a calendar empty-slot click
  prefillDuration = null, // minutes — set when opened by a DRAG-to-size on the diary (still editable)
  diaryDate = null,      // "YYYY-MM-DD" — the diary day being viewed (date for the time-only picker)
  providers = [],        // [{id, name, color}] — diary dentists (optional)
  prefillProvider = null, // {id,...} — column the empty slot was clicked in
  prefillPatient = null, // {id, full_name/name, account_code} — pre-select this patient (e.g. "Book appointment" from a patient profile)
  prefillEstimate = null, // {id, title, minutes, patient:{id,name,...}} — "Book" from the estimates tracker: hard-links the appt to the estimate + seeds patient/reason/duration
  startBlocked = false,  // open straight into block-out (Closed) mode — from the right-click "Block out time"
}) {
  const { t } = useLanguage()
  const isEdit = mode === 'edit'

  // A provider-scoped associate (a NON-admin user linked to a provider, e.g. Dr Eliska -> provider 2)
  // keeps full booking abilities but only ever under THEIR OWN provider. Default + LOCK the Dentist
  // field to them so a new/edited appointment can never be aimed at another dentist. This is UX +
  // defence-in-depth; AppointmentsController force-pins provider_id server-side on create/update too.
  const currentUser = usePage().props?.current_user
  const scopedProviderId = (currentUser && currentUser.role !== 'admin' && currentUser.provider_id != null) ? String(currentUser.provider_id) : null
  const scoped = scopedProviderId !== null

  // ── New-Patient inline path ───────────────────────────────────────
  // When reception clicks an empty calendar slot, the most common case
  // is "Mrs Smith just called — book her in for tomorrow 10am". We
  // don't want them to bounce to /patients first. The toggle below
  // lets them capture the patient inline:
  //   - Existing patient: pick from dropdown (the original flow)
  //   - New patient:      type first name + last name + phone here
  // Server-side AppointmentsController#create accepts both shapes.
  const [newPatientMode, setNewPatientMode] = useState(false)
  const [closedMode, setClosedMode] = useState(false) // block-out time (no patient)
  const [selectedPatient, setSelectedPatient] = useState(null) // type-ahead pick
  const [patientEstimates, setPatientEstimates] = useState([]) // the patient's linkable estimates
  // CONTINUITY OF CARE (Paul 2026-07-20): who this patient LAST SAW — null = not looked up yet,
  // { known: false } = looked up and there is genuinely no attended visit on record.
  const [lastSeen, setLastSeen] = useState(null)
  const [linkEstimateId, setLinkEstimateId] = useState('') // diary-side estimate link (Paul 2026-07-03)
  const [newFirstName,   setNewFirstName]   = useState('')
  const [newLastName,    setNewLastName]    = useState('')
  const [newPhone,       setNewPhone]       = useState('')
  const [phoneLoaded,    setPhoneLoaded]    = useState('') // what the edit form PREFILLED (a2_apptphone1016)
  // Layer 1 (Paul 2026-06-24): possible existing patients matching what reception is typing, so a
  // duplicate isn't created for someone already on file. Prompt-only — reception chooses; we never
  // auto-link. Populated by a debounced lookup against /patients/possible_duplicates.
  const [dupeMatches, setDupeMatches] = useState([])
  // outofhours1013 (D4): an off-hours NEW booking opens an in-modal choice instead of a bare confirm.
  const [offHoursChoice, setOffHoursChoice] = useState(null) // { provider_name, nearest: [iso], minutes, hours: [..], payload, opts }
  // "Save & book another" — keep the modal open after a successful create.
  const bookAnotherRef = useRef(false)
  const notesBox = useRef(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(buildSchema(t, () => newPatientMode || closedMode)),
    defaultValues: {
      patient_id: '',
      start_time: '',
      end_time: '',
      duration: 30,
      reason: '',
      notes: '',
      asap: false,
      provider_id: '',
    },
  })

  // ── Smart booking: snap start to 15 min, infer duration from reason, derive end ──
  const startTime = watch('start_time')
  const endTime   = watch('end_time')
  const reason    = watch('reason')
  const duration  = watch('duration')

  // Editable DATE: the day part of start_time, falling back to the clicked slot / diary day.
  const currentDate = startTime ? startTime.slice(0, 10)
    : (prefillStart ? dtLocal(prefillStart).slice(0, 10) : (diaryDate || dtLocal(new Date()).slice(0, 10)))
  // Duration: when the value isn't one of the presets (e.g. a long custom block, or an edited
  // appointment of an odd length) show the free-entry number box instead of the dropdown.
  const [customDuration, setCustomDuration] = useState(false)
  // Once the user explicitly picks a duration (preset or custom), stop the reason-driven
  // auto-suggest from overriding it. Bug: choosing "2 hours" then touching the Reason field
  // (e.g. "CLEANING AND FILLINGS") silently reset the block back to 30 min. (Ref so flipping it
  // doesn't re-render or re-fire the effects.)
  const durationTouchedRef = useRef(false)
  const durNum = Number(duration) || 0
  const isCustomDur = customDuration || (durNum > 0 && !DURATION_PRESETS.some((d) => d.m === durNum))

  // Next-available finder — fill Start with the next open slot for the chosen dentist.
  const [slotOptions, setSlotOptions] = useState([])
  const [findingSlots, setFindingSlots] = useState(false)
  const [slotsSearched, setSlotsSearched] = useState(false)
  const applySlot = (iso) => setValue('start_time', dtLocal(new Date(iso)), { shouldValidate: true, shouldDirty: true })
  const findNextAvailable = async () => {
    const providerId = watch('provider_id')
    if (!providerId) return
    setFindingSlots(true)
    try {
      const res = await fetch(`/appointments/next_available?provider_id=${providerId}&duration=${watch('duration') || 30}`, { headers: { Accept: 'application/json' } })
      const data = await res.json()
      const slots = data.slots || []
      setSlotOptions(slots)
      setSlotsSearched(true)
      if (slots[0]) applySlot(slots[0])
    } catch { /* ignore */ } finally { setFindingSlots(false) }
  }

  // Layer 1: while typing a NEW patient, look for existing records that match (ID → phone → name)
  // so reception can link instead of creating a duplicate. Debounced; prompt-only (never auto-links).
  useEffect(() => {
    if (isEdit || !newPatientMode) { setDupeMatches([]); return }
    const ln = newLastName.trim(); const ph = newPhone.replace(/\D/g, '')
    if (ln.length < 2 && ph.length < 6) { setDupeMatches([]); return }
    const ctrl = new AbortController()
    const id = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ first_name: newFirstName.trim(), last_name: ln, phone: newPhone.trim() })
        const res = await fetch(`/patients/possible_duplicates?${qs}`, { headers: { Accept: 'application/json' }, signal: ctrl.signal })
        if (!res.ok) return
        const data = await res.json()
        setDupeMatches(data.results || [])
      } catch { /* ignore (aborted/network) */ }
    }, 350)
    return () => { clearTimeout(id); ctrl.abort() }
  }, [newPatientMode, newFirstName, newLastName, newPhone, isEdit])

  // Reception confirms the typed "new" patient is actually an existing one → link to it instead of
  // creating a duplicate. (Deliberate click — we never auto-link.)
  const useExistingMatch = (m) => {
    setSelectedPatient({ id: m.id, full_name: m.name, account_code: m.account_code })
    setValue('patient_id', m.id, { shouldValidate: true })
    setNewPatientMode(false)
    setNewFirstName(''); setNewLastName(''); setNewPhone('')
    setDupeMatches([])
  }

  // Snap start_time to the nearest 15-minute interval whenever it changes.
  useEffect(() => {
    if (!startTime) return
    const snapped = snapTo15(startTime)
    if (snapped !== startTime) setValue('start_time', snapped)
  }, [startTime, setValue])

  // When reason text changes, suggest a duration that matches the practice's
  // typical slot length (check-up 45, whitening 90, etc.). Doesn't lock — the
  // user can still pick a different duration from the dropdown.
  // EDIT mode is exempt: a saved appointment's slot length is explicit, so we
  // must never auto-shrink it (a 90-min whitening reopened for edit was silently
  // becoming 30 min when this fired on hydration).
  useEffect(() => {
    if (isEdit) return
    if (!reason) return
    if (durationTouchedRef.current) return // user picked a duration explicitly — never override it
    setValue('duration', defaultDurationFor(reason))
  }, [reason, isEdit, setValue])

  // Whenever start_time or duration changes, derive end_time = start + duration.
  useEffect(() => {
    if (!startTime || !duration) return
    const d = new Date(startTime)
    d.setMinutes(d.getMinutes() + Number(duration))
    setValue('end_time', dtLocal(d))
  }, [startTime, duration, setValue])
  // outofhours1013: a changed time/length makes an open off-hours choice stale.
  useEffect(() => { setOffHoursChoice(null) }, [startTime, duration])

  // When the modal opens (or the target appointment changes) hydrate
  // the form with the right defaults. Resetting inside a useEffect
  // keeps the form controlled while still reacting to prop changes.
  useEffect(() => {
    if (!open) return
    if (isEdit && appointment) {
      // Seed duration from the appointment's REAL length so the derive-end effect
      // reproduces the saved end_time instead of clobbering it with a default.
      const mins = Math.round(
        (new Date(appointment.end_time) - new Date(appointment.start_time)) / 60000
      )
      reset({
        patient_id: appointment.patient_id || '',
        start_time: toLocalInput(appointment.start_time),
        end_time: toLocalInput(appointment.end_time),
        duration: mins > 0 ? mins : 30,
        reason: appointment.reason || '',
        notes: appointment.notes || '',
        // Scoped associate: pin to their own provider (their own appointments already are — belt & braces).
        provider_id: scoped ? scopedProviderId : (appointment.provider_id || ''),
      })
    } else {
      const start = prefillStart ? dtLocal(prefillStart) : ''
      // "Book" from the estimates tracker seeds the patient + reason (estimate title) + duration.
      const est = (!isEdit && prefillEstimate) || null
      reset({
        patient_id: (est?.patient?.id || prefillPatient?.id) || '', start_time: start, end_time: '',
        // A drag-to-size on the diary passes the painted length (prefillDuration) — still fully editable
        // in the field below (Paul 2026-08-20: "editable like any appointment — adjust time + duration").
        duration: prefillDuration || est?.minutes || 30, reason: est?.title || '', notes: '',
        // DEFAULT DENTIST = the PRINCIPAL when she is working that day (Paul 2026-07-20), else the
        // first dentist who is. Never defaults to someone on leave. A scoped associate always defaults
        // (and is locked) to THEIR OWN provider.
        provider_id: scoped ? scopedProviderId : (prefillProvider?.id || providers.find((p) => p.principal && !p.on_leave)?.id || providers.find((p) => !p.on_leave)?.id || providers[0]?.id || ''),
      })
    }
    setNewPatientMode(false)
    setClosedMode(startBlocked)   // right-click "Block out time" opens straight into block mode
    setOffHoursChoice(null)
    setCustomDuration(false)
    // Fresh open: in EDIT the seeded length is the user's explicit choice (lock it); in CREATE let
    // the reason auto-suggest run until they pick a duration themselves — but a booked-from-estimate
    // duration is deliberate, so lock it too.
    durationTouchedRef.current = !!(isEdit && appointment) || !!(prefillEstimate && prefillEstimate.minutes)
    // Create-mode: pre-select the patient when opened from their profile ("Book appointment") or the
    // estimates tracker ("Book") so reception never re-types the name (Paul 2026-06-26 / 07-02).
    setSelectedPatient(
      isEdit && appointment
        ? { id: appointment.patient_id, name: appointment.patient_name, phone: appointment.patient_phone } // seed the picker with the current patient so reception can swap to another
        : (!isEdit && (prefillPatient || prefillEstimate?.patient) ? (prefillPatient || prefillEstimate.patient) : null)
    )
    setLinkEstimateId((isEdit ? appointment?.estimate_id : prefillEstimate?.id) || '')
    if (isEdit && appointment) {
      // Prefill the patient's name + phone so reception can correct them in the edit form.
      setNewFirstName(appointment.patient_first_name || '')
      setNewLastName(appointment.patient_last_name || '')
      setNewPhone(appointment.patient_phone || ''); setPhoneLoaded(appointment.patient_phone || '')
    } else {
      setNewFirstName(''); setNewLastName(''); setNewPhone(''); setPhoneLoaded('')
    }
  }, [open, isEdit, appointment, prefillStart, prefillDuration, prefillProvider, prefillPatient, prefillEstimate, startBlocked, reset])

  // LINKING AN ESTIMATE SETS UP THE VISIT (Paul 2026-07-14). Reception was re-typing the reason
  // ("FILLINGS") and guessing a duration (120 min) while the estimate already knew both — and the
  // appointment could land in the wrong dentist's diary. The estimate is the source of truth: its title
  // is what we're doing, its chair time is how long it takes, and its dentist is whose plan it is.
  const linkedEstimate = patientEstimates.find((e) => String(e.id) === String(linkEstimateId)) || null
  // EDIT: reception picked a DIFFERENT patient in the picker → this appointment moves to that account.
  const reassigning = isEdit && selectedPatient && String(selectedPatient.id) !== String(appointment?.patient_id)

  const applyEstimate = (id) => {
    setLinkEstimateId(id)
    const est = patientEstimates.find((e) => String(e.id) === String(id))
    if (!est) return
    setValue('reason', est.title, { shouldValidate: true })
    if (Number(est.minutes) > 0) {
      const m = Number(est.minutes)
      setValue('duration', m, { shouldValidate: true })
      setCustomDuration(!DURATION_PRESETS.some((d) => d.m === m))
      // The ESTIMATE's chair time wins — do not let the reason-text guesser overwrite it afterwards.
      durationTouchedRef.current = true
    }
    if (est.provider_id) setValue('provider_id', String(est.provider_id), { shouldValidate: true })
  }

  // Load the patient's open estimates so reception can link this appointment to one (Paul 2026-07-03).
  useEffect(() => {
    const pid = isEdit ? appointment?.patient_id : selectedPatient?.id
    if (!open || !pid || newPatientMode) { setPatientEstimates([]); setLastSeen(null); return }
    let stop = false
    fetch(`/patients/${pid}/open_estimates`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (!stop) setPatientEstimates(Array.isArray(d) ? d : []) })
      .catch(() => {})
    // CONTINUITY: who did they last SEE? (attended visits only — a cancelled or merely-booked
    // appointment is not a visit, which is the whole point of the endpoint.)
    fetch(`/patients/${pid}/last_seen`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!stop) setLastSeen(d) })
      .catch(() => { if (!stop) setLastSeen(null) })
    return () => { stop = true }
  }, [open, isEdit, appointment?.patient_id, selectedPatient?.id, newPatientMode])

  const onSubmit = (data) => {
    // Closed / block-out: no patient — create a calendar note in this dentist's column.
    if (closedMode) {
      router.post('/calendar_notes', {
        calendar_note: {
          starts_at: new Date(data.start_time).toISOString(),
          ends_at: new Date(data.end_time).toISOString(),
          note: (data.reason || '').trim() || 'Closed',
          provider_id: data.provider_id || null,
        },
      }, {
        preserveScroll: true,
        // preserveState must be a CALLBACK, for the reason spelled out on the booking
        // path below: with a flat `false` the diary page remounts and this modal is
        // destroyed, so "keep the modal open" cannot work. Preserve on refusal only.
        preserveState: (page) => !!page?.props?.flash?.alert,
        // Same trap as the booking path above: a refused block-out redirects with an alert, which
        // Inertia delivers as a success. Believe the alert, not the redirect.
        onSuccess: (page) => {
          const alertMsg = page?.props?.flash?.alert
          if (alertMsg) { toast.error(alertMsg); return }
          toast.success('Time blocked out'); onClose?.()
        },
        onError: () => toast.error('Could not block out time'),
      })
      return
    }
    // New-patient inline path: pre-flight check on required fields
    // (the zod schema lets these through because they live OUTSIDE
    // the form — they're plain useState — to keep the schema simple).
    if (!isEdit && newPatientMode) {
      if (!newFirstName.trim() || !newLastName.trim() || !newPhone.trim()) {
        toast.error('New patient: first name, last name, and phone are required')
        return
      }
    }
    // Editing an appointment can also correct the patient's name — don't allow blanking it. Skipped when
    // re-linking to a different patient (we're not editing a name, we're picking an existing record).
    if (isEdit && !reassigning && (!newFirstName.trim() || !newLastName.trim())) {
      toast.error('First name and last name are required')
      return
    }

    const payload = {
      appointment: {
        ...(isEdit ? (reassigning ? { patient_id: selectedPatient.id } : {}) : { patient_id: newPatientMode ? null : data.patient_id }),
        start_time: new Date(data.start_time).toISOString(),
        end_time: new Date(data.end_time).toISOString(),
        reason: data.reason || null,
        notes: data.notes || null,
        provider_id: data.provider_id || null,
        asap: !!data.asap,
        // Hard-link to the estimate when this was opened via "Book" from the estimates tracker.
        estimate_id: linkEstimateId ? Number(linkEstimateId) : null,
      },
      // Server creates the patient first, then the appointment. Both
      // succeed or both roll back.
      ...(!isEdit && newPatientMode && {
        new_patient: {
          first_name: newFirstName.trim(),
          last_name:  newLastName.trim(),
          phone:      newPhone.trim(),
        },
      }),
      // Editing an existing appointment: save any corrections to the patient's name/phone too — but NOT
      // when re-linking to a different patient (that record keeps its own details).
      ...(isEdit && !reassigning && {
        patient: {
          first_name: newFirstName.trim(),
          last_name:  newLastName.trim(),
          // The phone goes only when reception changed it; the server also compares against phone_loaded.
          ...(newPhone.trim() !== phoneLoaded.trim() ? { phone: newPhone.trim() } : {}),
          phone_loaded: phoneLoaded.trim(),
        },
      }),
    }

    const opts = {
      preserveScroll: true,
      // 🚨 THIS MUST BE A CALLBACK, NOT `false`. (Found by script/frontend_check.cjs,
      // 2026-07-20 — the first time this path was ever driven in a real browser.)
      // The modal's open/closed state is `modalMode` — a useState INSIDE the diary page
      // component (CalendarFullscreen.jsx:28). With `preserveState: false`, Inertia
      // remounts that page component on the response, modalMode resets to null, and the
      // modal is destroyed no matter what onSuccess does. So the `return` below that was
      // meant to "keep the modal OPEN so the time can be corrected" silently did nothing:
      // the toast appeared and the half-filled form vanished anyway.
      // Preserve state exactly when the server REFUSED (an alert is present); discard it
      // on success so the diary remounts and shows the appointment that was just created.
      preserveState: (page) => !!page?.props?.flash?.alert,
      onSuccess: (page) => {
        // 🚨 A REJECTED BOOKING ARRIVES HERE AS A "SUCCESS". (Paul 2026-07-20.)
        // AppointmentsController answers a guard failure with `redirect_back ... alert:` and status 303.
        // Inertia FOLLOWS that redirect to a 200, so onError NEVER fires — this handler used to find no
        // flash.notice, fall through to toast.success("Appointment booked") and CLOSE the modal, over a
        // booking that was never saved. Paul booked Jessica Rooney 23 July 08:45–10:45 against Dr Eliska,
        // who already had 08:00, 10:00 and 10:45 that day; the server correctly said "This time slot
        // conflicts with an existing appointment" and he was shown a success and an empty diary.
        // The ALERT is the truth — check it before believing the redirect.
        const alertMsg = page?.props?.flash?.alert
        if (alertMsg) {
          toast.error(alertMsg)
          return // keep the modal OPEN so the time can be corrected instead of retyped from scratch
        }
        const notice = page?.props?.flash?.notice
        toast.success(notice || (isEdit ? t('modal_success_update') : t('modal_success_create')))
        if (bookAnotherRef.current && !isEdit) {
          // Keep the modal open for the next booking (e.g. another family member or a
          // back-to-back slot): clear the patient, start where this appointment ended.
          const nextStart = watch('end_time')
          bookAnotherRef.current = false
          setSelectedPatient(null); setNewPatientMode(false)
          setNewFirstName(''); setNewLastName(''); setNewPhone('')
          durationTouchedRef.current = false // next booking starts fresh — let reason auto-suggest again
          reset({ patient_id: '', start_time: nextStart || '', end_time: '', duration: 30, reason: '', notes: '', provider_id: watch('provider_id') || '' })
        } else {
          onClose?.()
        }
      },
      onError: (errs) => {
        const msg = Object.values(errs || {})[0] || t('modal_error_generic')
        toast.error(msg)
      },
    }

    if (isEdit) {
      // Off-hours on an EDIT: if the new time is outside the dentist's scheduled hours, confirm ONCE and
      // tell the server the person confirmed (override_off_hours), so editing an appointment to e.g. 4pm is
      // never a dead "no" — the #update guard rejects a confirmed edit that carries no override (Paul
      // 2026-08-16: "I want to book the patient for 4pm manually. Stop saying no."). A person can always
      // place a booking off-hours; the confirm is the hard-confirm. A failed/absent slot-check never blocks.
      const eProv = payload.appointment.provider_id
      const eStart = payload.appointment.start_time
      // 🚨 ASK BEFORE MESSAGING THE PATIENT ABOUT A MOVED TIME (Paul 2026-08-21). Only when the start
      // time actually changed — an edit to notes/reason/duration never messages them. OK = send the
      // new-time WhatsApp · Cancel = change it quietly.
      const timeMoved = !!(appointment && eStart && new Date(eStart).getTime() !== new Date(appointment.start_time).getTime())
      const askNotify = () => (timeMoved
        ? window.confirm(`Send ${appointment.patient_name || 'the patient'} a WhatsApp about the NEW time?\n\nOK = Yes, tell them the new time.\nCancel = No, change it quietly (don't message them).`)
        : true)
      if (!closedMode && eProv && eStart) {
        fetch(`/appointments/slot_check?provider_id=${eProv}&start=${encodeURIComponent(eStart)}`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((res) => {
            const off = !!(res && res.off_hours)
            if (off && !window.confirm(`${res.provider_name || 'This dentist'} isn't scheduled to work at that time. Book anyway?`)) return
            router.patch(`/appointments/${appointment.id}`, { ...payload, override_off_hours: off, notify_reschedule: askNotify() }, opts)
          })
          .catch(() => router.patch(`/appointments/${appointment.id}`, { ...payload, override_off_hours: true, notify_reschedule: askNotify() }, opts))
      } else {
        router.patch(`/appointments/${appointment.id}`, { ...payload, notify_reschedule: askNotify() }, opts)
      }
      return
    }
    // Off-hours soft-warn (Paul 2026-07-29): if placing OUTSIDE the chosen dentist's scheduled hours, ask
    // first — the diary click/paste/drag already warn; this covers the blank New-appointment form. Skips
    // block-out mode + when no provider chosen; a failed/absent check never blocks a booking.
    // outofhours1013 (standing order D4): the WHOLE visit is checked (start, end, breaks). Outside the dentist's hours →
    // an in-modal choice: her nearest in-hours times, or "Dentist agreed — book anyway" (writes "dentist agreed (<user>)"
    // into the notes). An old-shape reply (kill switch) keeps the old confirm.
    const provId = payload.appointment.provider_id
    const startISO = payload.appointment.start_time
    if (!closedMode && provId && startISO) {
      const qs = new URLSearchParams({ provider_id: provId, start: startISO, end: payload.appointment.end_time || '', reason: payload.appointment.reason || '' })
      if (payload.appointment.estimate_id) qs.set('estimate_id', payload.appointment.estimate_id)
      fetch(`/appointments/slot_check?${qs}`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((res) => {
          if (res && res.off_hours && Array.isArray(res.nearest)) { setOffHoursChoice({ ...res, payload, opts }); return }
          if (res && res.off_hours && !window.confirm(`${res.provider_name || 'This dentist'} isn't scheduled to work at that time. Book anyway?`)) return
          router.post('/appointments', payload, opts)
        })
        .catch(() => router.post('/appointments', payload, opts))
    } else {
      router.post('/appointments', payload, opts)
    }
  }

  // outofhours1013: reception's answer to the off-hours choice.
  const pickNearestTime = (iso) => {
    const mins = Number(offHoursChoice?.minutes) || Number(watch('duration')) || 30
    setOffHoursChoice(null)
    durationTouchedRef.current = true // the treatment-table length wins; the reason guesser must not shrink it
    setCustomDuration(!DURATION_PRESETS.some((d) => d.m === mins))
    const start = new Date(iso)
    const end = new Date(start.getTime() + mins * 60000)
    setValue('duration', mins, { shouldDirty: true })
    setValue('start_time', dtLocal(start), { shouldValidate: true, shouldDirty: true })
    setValue('end_time', dtLocal(end), { shouldValidate: true })
    setTimeout(() => handleSubmit(onSubmit)(), 0) // submit normally: re-checks the hours, then books
  }
  const bookDentistAgreed = () => {
    const c = offHoursChoice
    if (!c) return
    setOffHoursChoice(null)
    const notes = [c.payload.appointment.notes, `dentist agreed (${currentUser?.name || 'reception'})`].filter(Boolean).join(' · ')
    setValue('notes', notes)
    router.post('/appointments', { ...c.payload, appointment: { ...c.payload.appointment, notes }, override_off_hours: true }, c.opts)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('modal_edit_appointment') : t('modal_new_appointment')}
      size="lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-4 py-2 text-sm font-medium text-brand-muted transition-colors hover:bg-brand-surface/45 hover:text-brand-ink"
          >
            {t('modal_cancel_btn')}
          </button>
          {!isEdit && !closedMode && (
            <button
              type="submit"
              form="appointment-form"
              onClick={() => { bookAnotherRef.current = true }}
              disabled={isSubmitting}
              className="rounded-2xl border border-brand-border px-4 py-2 text-sm font-medium text-brand-ink transition-colors hover:bg-brand-surface disabled:opacity-50"
              title="Save this appointment and keep booking (next slot, same dentist) — e.g. another family member"
            >
              Save &amp; book another
            </button>
          )}
          <button
            type="submit"
            form="appointment-form"
            onClick={() => { bookAnotherRef.current = false }}
            disabled={isSubmitting}
            className="rounded-2xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-[0_18px_35px_-24px_rgba(49,100,222,0.9)] transition-colors hover:bg-brand-primary-dark disabled:opacity-50"
          >
            {isEdit ? t('modal_save_btn') : (closedMode ? 'Block out time' : t('modal_book_btn'))}
          </button>
        </>
      }
    >
      <form id="appointment-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Patient — toggle between Existing (picker) and New (inline capture) */}
        {!isEdit && (
          <div className="space-y-2">
            <div className="inline-flex rounded-2xl border border-brand-border bg-brand-surface p-1">
              <button type="button" onClick={() => { setNewPatientMode(false); setClosedMode(false) }}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${
                  !newPatientMode && !closedMode ? 'bg-white text-brand-ink shadow-sm' : 'text-brand-muted hover:text-brand-ink'
                }`}>
                <Users size={13} /> Existing patient
              </button>
              <button type="button" onClick={() => { setNewPatientMode(true); setClosedMode(false) }}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${
                  newPatientMode && !closedMode ? 'bg-white text-brand-primary shadow-sm' : 'text-brand-muted hover:text-brand-ink'
                }`}>
                <UserPlus size={13} /> New patient
              </button>
              <button type="button" onClick={() => { setClosedMode(true); setNewPatientMode(false) }}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${
                  closedMode ? 'bg-white text-pink-600 shadow-sm' : 'text-brand-muted hover:text-brand-ink'
                }`}>
                <Ban size={13} /> Closed / block
              </button>
            </div>

            {closedMode ? (
              <p className="rounded-2xl border border-brand-accent/60 bg-brand-surface/40 px-3 py-2.5 text-xs text-brand-muted">
                Blocking out time (e.g. lunch, leave, closed) in the selected dentist’s column — no patient needed. Type a label in “Reason”.
              </p>
            ) : !newPatientMode ? (
              <Field label={t('modal_patient_label')} error={errors.patient_id?.message}>
                <input type="hidden" {...register('patient_id')} />
                <PatientSearchSelect
                  selected={selectedPatient}
                  autoFocus
                  onSelect={(p) => {
                    setSelectedPatient(p)
                    setValue('patient_id', p?.id || '', { shouldValidate: true })
                  }}
                />
              </Field>
            ) : (
              <div className="grid grid-cols-3 gap-3 rounded-2xl border border-brand-accent/80 bg-brand-surface/40 p-3">
                <Field label="First name">
                  <input value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)}
                    autoFocus
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                <Field label="Last name">
                  <input value={newLastName} onChange={(e) => setNewLastName(e.target.value)}
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+27 82 123 4567"
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                {dupeMatches.length > 0 && (
                  <div className="col-span-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
                    <p className="text-xs font-semibold text-amber-800">
                      ⚠ Possible existing patient{dupeMatches.length > 1 ? 's' : ''} — link instead of creating a duplicate:
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {dupeMatches.map((m) => (
                        <li key={m.id} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-amber-900">
                            <span className="font-medium">{m.name}</span>
                            {m.account_code ? ` · ${m.account_code}` : ' · no account'}
                            {m.reason ? ` · ${m.reason}` : ''}
                          </span>
                          <button type="button" onClick={() => useExistingMatch(m)}
                            className="flex-shrink-0 rounded-lg border border-amber-400 bg-white px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100">
                            Use this patient
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1.5 text-[11px] text-amber-700">Not a match? Carry on — this will create a new record.</p>
                  </div>
                )}
                <p className="col-span-3 text-xs text-brand-muted">
                  This will create a new patient record and book the appointment in one step. You can fill in ID number, medical aid, and medical history later from the patient profile.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Diary-side estimate link: attach this appointment to a patient's open estimate so the
            estimates tracker flips it to "Booked" (Paul 2026-07-03). */}
        {!closedMode && !newPatientMode && patientEstimates.length > 0 && (
          <div className="rounded-2xl border-2 border-brand-primary/50 bg-brand-surface/40 p-3">
            <Field label="Link to an estimate — the visit is then set up from it">
              <select value={linkEstimateId} onChange={(e) => applyEstimate(e.target.value)}
                className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45">
                <option value="">— none —</option>
                {patientEstimates.map((es) => (
                  <option key={es.id} value={es.id}>
                    {es.title} · R{Number(es.total).toFixed(2)} · {es.minutes} min{es.booked ? ' · already booked' : ''}
                  </option>
                ))}
              </select>
            </Field>
            {linkedEstimate ? (
              <p className="mt-1.5 px-1 text-[11px] font-semibold text-brand-primary">
                Reason, chair time ({linkedEstimate.minutes} min){linkedEstimate.provider_name ? ` and dentist (${linkedEstimate.provider_name})` : ''} taken from this estimate. It will show as “Booked” on the tracker.
              </p>
            ) : (
              <p className="mt-1 px-1 text-[11px] text-brand-muted">Pick the treatment they are coming in for — we then set the reason, the chair time and the dentist from it, so nothing is re-typed or guessed.</p>
            )}
          </div>
        )}

        {/* Edit mode: pick a DIFFERENT patient to MOVE this appointment (same picker as New), or edit the
            current patient's name/phone in place. (Paul 2026-07-31) */}
        {isEdit && (
          <div className="space-y-3 rounded-2xl border border-brand-accent/80 bg-brand-surface/40 p-3">
            <Field label="Patient — search to move this appointment to a different person / account">
              <PatientSearchSelect
                selected={selectedPatient}
                onSelect={(p) => { setSelectedPatient(p); setValue('patient_id', p?.id || '') }}
              />
            </Field>
            {reassigning ? (
              <p className="rounded-xl border border-brand-primary/40 bg-white/60 px-2.5 py-2 text-xs text-brand-primary">
                This appointment will move to <span className="font-semibold">{selectedPatient.name}</span>. Their own saved details are used — no need to retype the name.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <Field label="First name">
                  <input value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)}
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                <Field label="Last name">
                  <input value={newLastName} onChange={(e) => setNewLastName(e.target.value)}
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                <Field label="Phone">
                  <input type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="+27 82 123 4567"
                    className="w-full rounded-xl border border-brand-accent/80 bg-white px-2.5 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45" />
                </Field>
                <p className="col-span-3 text-xs text-brand-muted">
                  Editing these updates the patient’s record. ID number, medical aid and history are on the patient profile.
                </p>
              </div>
            )}
          </div>
        )}

        {providers.length > 0 && (
          <Field label="Dentist">
            {scoped ? (
              // Scoped associate: the Dentist is LOCKED to their own provider (they only ever book
              // their own diary). Shown read-only; provider_id is already pinned in the form state.
              <div className="w-full rounded-2xl border border-brand-border bg-brand-surface/40 px-3 py-2.5 text-sm text-brand-muted">
                {providers.find((p) => String(p.id) === scopedProviderId)?.name || currentUser?.name || 'Your diary'}
                <span className="ml-1 text-xs">— your diary</span>
              </div>
            ) : (
              <select
                {...register('provider_id')}
                className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.on_leave ? ' — on leave (override)' : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}

        {/* ── CONTINUITY OF CARE (Paul 2026-07-20) ───────────────────────────────────────────
            "If we are booking an existing patient, the system shows us who the dentist they
            should be booking with and who they saw the previous time."

            🚨 INFORMATIVE, NOT COERCIVE — Paul asked explicitly that human judgement be
            preserved, and the live data backs him: every continuity break in this diary's
            history was a deliberate move of high-value work to Dr Chalita. So this panel never
            changes the dropdown by itself. It states the fact, and when the current selection
            disagrees it offers ONE TAP to switch — a suggestion reception can ignore. */}
        {/* Pre-visit AI briefing (Paul 2026-08-08): why they're really coming / what they said on the
            phone + WhatsApp / what they expect. Built by the LOCAL model, read-only, never the notes field. */}
        {isEdit && appointment?.ai_briefing && (
          ['why', 'said', 'expectation'].some((k) => (appointment.ai_briefing[k] || []).length > 0)
        ) && (
          <div className="rounded-2xl border border-indigo-300 bg-indigo-50 px-3 py-2.5 text-xs mb-1">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-indigo-700">🧠 Briefing — before they sit down</span>
              <span className="inline-flex items-center gap-2">
                <span className="text-[10px] text-indigo-400">
                  from {((appointment.ai_briefing.sources || []).map((s) => s.type).filter((v, i, a) => a.indexOf(v) === i).join(' + ')) || 'their messages'}
                </span>
                <button type="button"
                  onClick={() => router.post(`/appointments/${appointment.id}/refresh_briefing`, {}, { preserveScroll: true, onSuccess: () => toast.success('Briefing updated — reopen the appointment to see it') })}
                  className="text-[10px] text-indigo-500 hover:text-indigo-700 underline">↻ Refresh</button>
              </span>
            </div>
            {/* Shared-number safety (Paul 2026-08-08): warn before the dentist trusts a briefing that may
                be about a different family member on the same phone. */}
            {appointment.ai_briefing.verify && (
              <div className="mt-1 rounded bg-amber-100 border border-amber-300 px-2 py-1 text-[11px] text-amber-800">
                ⚠️ This phone number is shared by more than one patient — double-check this briefing is about {appointment.patient_name || 'this patient'}.
              </div>
            )}
            {[['why', 'Why they’re coming'], ['said', 'What they said'], ['expectation', 'What they expect']].map(([k, label]) => (
              (appointment.ai_briefing[k] || []).length > 0 && (
                <div key={k} className="mt-1.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500">{label}</p>
                  <ul className="list-disc list-inside text-indigo-900 space-y-0.5 mt-0.5">
                    {(appointment.ai_briefing[k] || []).map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )
            ))}
          </div>
        )}

        {!closedMode && !newPatientMode && lastSeen && (
          lastSeen.known ? (
            <div className="rounded-2xl border border-brand-accent/60 bg-brand-surface/40 px-3 py-2.5 text-xs">
              <div className="text-brand-ink">
                Last seen by <span className="font-semibold">{lastSeen.provider_name}</span>
                {lastSeen.on_human ? <> on <span className="font-semibold">{lastSeen.on_human}</span></> : null}
                {lastSeen.reason ? <span className="text-brand-muted"> — {lastSeen.reason}</span> : null}
              </div>
              {!scoped && String(watch('provider_id') || '') !== String(lastSeen.provider_id) && (
                <div className="mt-1.5 flex items-center gap-2">
                  <span className="text-brand-muted">
                    This booking is with a different dentist.
                  </span>
                  <button
                    type="button"
                    onClick={() => setValue('provider_id', String(lastSeen.provider_id), { shouldValidate: true })}
                    className="rounded-lg border border-brand-primary/50 px-2 py-1 font-medium text-brand-primary hover:bg-brand-primary/10"
                  >
                    Book with {lastSeen.provider_name}
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* "We don't know" is an ANSWER, not a blank: 54% of this practice's patients with
               appointments have no attended visit on record, so reception must be able to tell a
               genuine first-timer from a panel that simply failed to load. */
            <p className="text-xs text-brand-muted">
              No previous visit on record — first appointment with the practice.
            </p>
          )
        )}

        {/* Hidden REGISTERED fields so react-hook-form tracks + submits start/end. The visible
            Start dropdown + derived End set these via setValue; without an explicit register RHF
            dropped them from the submit (empty payload => bookings silently failed). */}
        <input type="hidden" {...register('start_time')} />
        <input type="hidden" {...register('end_time')} />

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Date" error={errors.start_time?.message}>
            {/* Editable DATE — move a booking to another day right here (no Ctrl+C/Ctrl+V needed). */}
            <input
              type="date"
              value={currentDate}
              onChange={(e) => {
                if (!e.target.value) return
                const timePart = startTime ? startTime.slice(11, 16) : ''
                setValue('start_time', timePart ? `${e.target.value}T${timePart}` : `${e.target.value}T00:00`, { shouldValidate: true, shouldDirty: true })
              }}
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
            />
          </Field>
          <Field label={t('modal_start_time')} error={errors.start_time?.message}>
            {/* TIME, in 15-min slots — combined with the Date field above into start_time. */}
            <select
              value={startTime ? startTime.slice(11, 16) : ''}
              onChange={(e) => {
                setValue('start_time', e.target.value ? `${currentDate}T${e.target.value}` : '', { shouldValidate: true, shouldDirty: true })
              }}
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
            >
              <option value="">— time —</option>
              {(startTime && !TIME_SLOTS.includes(startTime.slice(11, 16)) ? [startTime.slice(11, 16), ...TIME_SLOTS] : TIME_SLOTS).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Duration">
            {/* No cap — long presets + "Custom…" for any length (e.g. blocking a whole day). */}
            <select
              value={isCustomDur ? 'custom' : String(durNum)}
              onChange={(e) => {
                durationTouchedRef.current = true
                if (e.target.value === 'custom') { setCustomDuration(true) }
                else { setCustomDuration(false); setValue('duration', Number(e.target.value), { shouldDirty: true }) }
              }}
              className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
            >
              {DURATION_PRESETS.map((d) => (
                <option key={d.m} value={d.m}>{d.label}</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
            {isCustomDur && (
              <input
                type="number" min="15" step="5" value={durNum || ''} placeholder="minutes"
                onChange={(e) => { durationTouchedRef.current = true; setValue('duration', Math.max(Number(e.target.value) || 0, 0), { shouldDirty: true }) }}
                className="mt-1 w-full rounded-xl border border-brand-accent/80 bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
              />
            )}
          </Field>
          <Field label={t('modal_end_time')} error={errors.end_time?.message}>
            <input
              type="time"
              readOnly
              value={endTime ? endTime.slice(11, 16) : ''}
              className="w-full rounded-2xl border border-brand-border bg-brand-surface/60 px-3 py-2.5 text-sm text-brand-muted focus:outline-none"
              title="Auto-set from Start + Duration"
            />
          </Field>
        </div>

        {/* TAKE A DEPOSIT reminder (Paul 2026-08-25) — any booking of 2 hours or more, for ANY dentist,
            is long/high-value chair time, so reception must take a deposit when the booking is made. A
            block-out (no patient) never applies. Shown at booking time; does not block the save. */}
        {durNum >= 120 && !closedMode && (
          <div className="flex items-start gap-2 rounded-2xl border-2 border-amber-400 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <span className="text-lg leading-none">💰</span>
            <div>
              <span className="font-bold uppercase tracking-wide">Take a deposit</span>
              <span className="block text-[13px] leading-tight">
                This is a {Math.floor(durNum / 60)}h{durNum % 60 ? ` ${durNum % 60}m` : ''} booking — remember to take a deposit from the patient when you make it.
              </span>
            </div>
          </div>
        )}

        {/* Next-available finder — fills Start with the next open slot for this dentist */}
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={findNextAvailable} disabled={findingSlots || !watch('provider_id')}
            className="inline-flex items-center gap-1 rounded-lg border border-brand-primary bg-brand-primary/5 px-3 py-1.5 text-xs font-semibold text-brand-primary transition hover:bg-brand-primary/10 disabled:opacity-50">
            {findingSlots ? 'Finding…' : '⚡ Find next available'}
          </button>
          {slotOptions.slice(1).map((s) => (
            <button key={s} type="button" onClick={() => applySlot(s)}
              className="rounded-lg border border-brand-border px-2.5 py-1.5 text-xs text-brand-ink transition hover:bg-brand-surface">
              {new Date(s).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </button>
          ))}
          {slotsSearched && slotOptions.length === 0 && !findingSlots && (
            <span className="text-xs text-brand-muted">No open slots in the next 30 days for this dentist.</span>
          )}
        </div>

        <Field label={t('modal_reason')}>
          <input
            type="text"
            placeholder={t('modal_reason_placeholder')}
            {...register('reason')}
            className="w-full rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
          />
        </Field>

        <Field label={t('modal_notes')}>
          <textarea
            rows={3}
            placeholder={t('modal_notes_placeholder')}
            {...register('notes')}
            ref={(el) => { register('notes').ref(el); notesBox.current = el }}
            className="w-full resize-none rounded-2xl border border-brand-accent/80 bg-white px-3 py-2.5 text-sm text-brand-ink focus:border-brand-primary focus:outline-none focus:ring-4 focus:ring-brand-accent/45"
          />
          <DictateButton targetRef={notesBox} onText={(v) => setValue('notes', v, { shouldDirty: true })} context="appointment_notes" className="mt-1" />
        </Field>

        {!closedMode && (
          <label className="flex items-center gap-2 text-sm text-brand-ink">
            <input type="checkbox" {...register('asap')} className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary" />
            <span>Patient wants an <strong>earlier slot</strong> if one opens (adds to the ASAP list)</span>
          </label>
        )}
        {offHoursChoice && (
          <div role="alert" className="space-y-2 rounded-2xl border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-brand-ink">
            <p>
              <strong>{offHoursChoice.provider_name || 'This dentist'}</strong> does not work{' '}
              {new Date(offHoursChoice.payload.appointment.start_time).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}–{new Date(offHoursChoice.payload.appointment.end_time).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
              {' '}(hours that day: {(offHoursChoice.hours || []).join(', ') || 'off'}).{' '}
              {offHoursChoice.nearest.length ? 'Nearest in her hours:' : 'No free time in her hours in the next 10 working days.'}
            </p>
            <div className="flex flex-wrap gap-2">
              {offHoursChoice.nearest.map((iso) => (
                <button key={iso} type="button" onClick={() => pickNearestTime(iso)}
                  className="rounded-lg border border-brand-primary bg-white px-3 py-1.5 text-xs font-semibold text-brand-primary hover:bg-brand-primary/10">
                  {new Date(iso).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </button>
              ))}
              <button type="button" onClick={bookDentistAgreed}
                className="rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100">
                Dentist agreed — book anyway
              </button>
              <button type="button" onClick={() => setOffHoursChoice(null)}
                className="rounded-lg px-3 py-1.5 text-xs text-brand-muted hover:text-brand-ink">
                Cancel
              </button>
            </div>
          </div>
        )}
      </form>
    </Modal>
  )
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-brand-muted">
        {label}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-brand-danger">{error}</span>}
    </label>
  )
}
